import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

export type CampaignType = 'SEARCH' | 'DISPLAY' | 'PERFORMANCE_MAX' | 'SHOPPING' | 'DEMAND_GEN';
export type CampaignStatus = 'PAUSED' | 'ENABLED';

interface CampaignBasicsStepProps {
  campaignName: string;
  campaignType: CampaignType;
  status: CampaignStatus;
  containsEuPoliticalAdvertising: boolean;
  onChangeName: (name: string) => void;
  onChangeType: (type: CampaignType) => void;
  onChangeStatus: (status: CampaignStatus) => void;
  onChangeEuPoliticalAdvertising: (value: boolean) => void;
  errors?: {
    name?: string;
  };
}

// Campaign types that are not yet supported - remove from this array once implemented
// TODO: Remove 'SHOPPING' from this list once support is added
const UNSUPPORTED_CAMPAIGN_TYPES: CampaignType[] = ['SHOPPING'];

const ALL_CAMPAIGN_TYPES: Array<{ value: CampaignType; label: string; description: string }> = [
  {
    value: 'SEARCH',
    label: 'Search',
    description: 'Show text ads on Google Search results',
  },
  {
    value: 'DISPLAY',
    label: 'Display',
    description: 'Show image and text ads across the web',
  },
  {
    value: 'PERFORMANCE_MAX',
    label: 'Performance Max',
    description: 'AI-powered campaigns across all Google channels',
  },
  {
    value: 'SHOPPING',
    label: 'Shopping',
    description: 'Promote your products on Google Shopping',
  },
  {
    value: 'DEMAND_GEN',
    label: 'Demand Gen',
    description: 'Drive demand across YouTube, Discover, Gmail, and Display',
  },
];

// Filter out unsupported campaign types
const CAMPAIGN_TYPES = ALL_CAMPAIGN_TYPES.filter(
  (type) => !UNSUPPORTED_CAMPAIGN_TYPES.includes(type.value)
);

const CAMPAIGN_STATUSES: Array<{ value: CampaignStatus; label: string; description: string }> = [
  {
    value: 'PAUSED',
    label: 'Paused',
    description: 'Campaign will not serve ads (recommended for new campaigns)',
  },
  {
    value: 'ENABLED',
    label: 'Enabled',
    description: 'Campaign will start serving ads immediately',
  },
];

export const CampaignBasicsStep: React.FC<CampaignBasicsStepProps> = ({
  campaignName,
  campaignType,
  status,
  containsEuPoliticalAdvertising,
  onChangeName,
  onChangeType,
  onChangeStatus,
  onChangeEuPoliticalAdvertising,
  errors,
}) => {
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Basics</h3>
        <p className="text-sm text-gray-600">
          Set up the basic details for your new campaign.
        </p>
      </div>

      {/* Campaign Name */}
      <div>
        <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700 mb-2">
          Campaign Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="campaign-name"
          type="text"
          value={campaignName}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g., Summer Sale 2025"
          maxLength={255}
          className={cn({
            'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.name,
          })}
          aria-invalid={!!errors?.name}
          aria-describedby={errors?.name ? 'campaign-name-error' : undefined}
        />
        {errors?.name && (
          <p id="campaign-name-error" className="mt-1 text-sm text-red-600">
            {errors.name}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Choose a descriptive name to identify this campaign. {campaignName.length}/255 characters.
        </p>
      </div>

      {/* Campaign Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Campaign Type <span className="text-red-500">*</span>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          {CAMPAIGN_TYPES.map((type) => {
            const isSelected = campaignType === type.value;
            return (
              <label
                key={type.value}
                className={cn(
                  'relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all',
                  'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <input
                  type="radio"
                  name="campaign-type"
                  value={type.value}
                  checked={isSelected}
                  onChange={() => onChangeType(type.value)}
                  className="sr-only"
                  aria-describedby={`type-${type.value}-description`}
                />
                <div className="flex items-start">
                  <div
                    className={cn(
                      'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5',
                      {
                        'border-blue-600 bg-blue-600': isSelected,
                        'border-gray-300': !isSelected,
                      }
                    )}
                  >
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-white" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900">{type.label}</span>
                    <p
                      id={`type-${type.value}-description`}
                      className="text-xs text-gray-500 mt-1"
                    >
                      {type.description}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Campaign Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Initial Status <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {CAMPAIGN_STATUSES.map((statusOption) => {
            const isSelected = status === statusOption.value;
            return (
              <label
                key={statusOption.value}
                className={cn(
                  'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all',
                  'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <input
                  type="radio"
                  name="campaign-status"
                  value={statusOption.value}
                  checked={isSelected}
                  onChange={() => onChangeStatus(statusOption.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                  aria-describedby={`status-${statusOption.value}-description`}
                />
                <div className="ml-3 flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {statusOption.label}
                  </span>
                  <p
                    id={`status-${statusOption.value}-description`}
                    className="text-xs text-gray-500 mt-1"
                  >
                    {statusOption.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* EU Political Advertising Compliance */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            checked={containsEuPoliticalAdvertising}
            onChange={(e) => onChangeEuPoliticalAdvertising(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
          />
          <div className="ml-3 flex-1">
            <span className="block text-sm font-medium text-gray-900">
              Contains EU Political Advertising
            </span>
            <p className="text-xs text-gray-600 mt-1">
              Check this box if your campaign contains political advertising targeting the European Union. Required for compliance with EU regulations.
            </p>
          </div>
        </label>
      </div>
    </div>
  );
};
